import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiResponse, enterGroupChat } from '~/api';
import { useGroupChatErrorHandler } from '~/hooks';
import { ApiError, EmptyObject, EnterGroupChatRequest } from '~/types';
import chatRoomKey from './chatRoomKey';

const useEnterGroupChatMutation = () => {
  const queryClient = useQueryClient();
  const { handleChatError } = useGroupChatErrorHandler();

  return useMutation<ApiResponse<EmptyObject>, ApiError, EnterGroupChatRequest>(
    {
      mutationFn: (requestData: EnterGroupChatRequest) =>
        enterGroupChat(requestData),

      onSuccess: (_, request) => {
        const queryKey = [...chatRoomKey.all, 'group'];

        queryClient.setQueriesData(
          { queryKey, exact: false },
          (oldData: any) => {
            if (!oldData?.pages) return oldData;

            const incrementParticipants = (chatRoom: any) =>
              chatRoom.id === request.chatroom_id
                ? {
                    ...chatRoom,
                    participants_num: String(
                      Number(chatRoom.participants_num) + 1,
                    ),
                  }
                : chatRoom;

            if (oldData.pages[0]?.status === 200) {
              return {
                ...oldData,
                pages: oldData.pages.map((page: any) =>
                  !page?.data
                    ? page
                    : {
                        ...page,
                        data: Array.isArray(page.data)
                          ? page.data.map(incrementParticipants)
                          : page.data,
                      },
                ),
              };
            }

            if (Array.isArray(oldData.pages) && oldData.pages[0]?.id) {
              return {
                ...oldData,
                pages: oldData.pages.map(incrementParticipants),
              };
            }

            return oldData;
          },
        );
      },

      onError: (error) => {
        console.error('Group chat entry failed:', error);
        console.error(error);
        handleChatError(error.status);
      },
    },
  );
};

export default useEnterGroupChatMutation;
